import Keycloak from 'keycloak-js'
import { dropOAuthCallbackFromLocation, markSignInStarted, takeSignInStarted, withoutOAuthCallback } from './oauthCallback'

const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8282/auth',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'lupa',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'lupa-frontend',
})

// Every login redirect — ours and keycloak-js's own check-sso, which calls `this.login` — goes
// through here. A redirect URI never carries a previous callback's `#state=…&code=…` (see
// oauthCallback.ts for the 502 that caused), and a sign-in the user asked for is remembered for
// the trip so the site root can take them to the cabinet when they come back.
const startLogin = keycloak.login
keycloak.login = (options) => {
  markSignInStarted(options?.prompt)
  return startLogin({ ...options, redirectUri: withoutOAuthCallback(options?.redirectUri ?? window.location.href) })
}

let returnedFromSignIn = false

/**
 * True when this page load is the return from a sign-in the user started, landing on the site
 * root. Only then does the root open the cabinet instead of the landing.
 */
export function isReturnFromSignIn(): boolean {
  return returnedFromSignIn
}

/** One redirect per sign-in: a later visit to the root in the same tab shows the landing. */
export function clearReturnFromSignIn(): void {
  returnedFromSignIn = false
}

// The local public-track mock deliberately runs without Keycloak. It gives designers and
// developers a reliable visual review URL even when the local SSO server is stopped; this
// branch is compiled out of production and never applies to a real shared tracking link.
const isLocalPublicTrackPreview = import.meta.env.DEV &&
  window.location.pathname === '/track/preview' &&
  new URLSearchParams(window.location.search).get('demo') === '1'

// Explicit sign-in establishes the SSO cookie; startup restores it in a hidden iframe.
// Do not fall back to a full-page check when cookies are blocked: public pages must stay
// in place. The sign-in button and AuthGuard still use the normal login redirect.
//
// Registration used to short-circuit this by adopting the tokens the backend obtains via a
// direct grant (they were handed over in a one-shot sessionStorage key). Those tokens are
// valid, but the exchange happens server-to-server — the browser never gets an SSO cookie,
// so the very next reload found no session here and logged the user straight out. The
// registration flow now sends the user through the login page instead; mobile still uses the
// backend's tokens, where a cookie-less session is the norm and nothing depends on check-sso.
export const keycloakInitPromise: Promise<boolean> = isLocalPublicTrackPreview
  ? Promise.resolve(false)
  : keycloak.init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: new URL('/silent-check-sso.html', window.location.origin).href,
      silentCheckSsoFallback: false,
      pkceMethod: 'S256',
    }).then(
      (authenticated) => {
        returnedFromSignIn = takeSignInStarted() && authenticated && window.location.pathname === '/'
        return authenticated
      },
      (error: unknown) => {
        takeSignInStarted()
        // init() gave up before it cleaned the address bar (its 3p-cookie iframe timed out on a
        // bad network). Do it here, or a reload replays the spent callback.
        dropOAuthCallbackFromLocation()
        throw error
      },
    )

export default keycloak
